import type {
    MediaItem,
} from "@mrflark/spl_service_model";
import {
    Skeleton,
} from "@mrflark/ui";
import {
    Download,
    Maximize2,
    Minimize2,
    Sparkles,
} from "lucide-preact";
import {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "preact/compat";
import {
    toast,
} from "react-hot-toast";

const getFileExtension = (url: string, mediaType: string): string => {
    try {
        const pathname = new URL(url).pathname;
        const lastDot = pathname.lastIndexOf(".");
        if (lastDot !== -1) {
            const ext = pathname.substring(lastDot + 1).toLowerCase();
            if (ext.length >= 2 && ext.length <= 5) return ext;
        }
    } catch { /* ignore */ }
    return mediaType === "video" ? "mp4" : "jpg";
};

type MediaViewerProps = {
    item: MediaItem | null;
    isLoading: boolean;
    showOcrOverlay?: boolean;
    onToggleAiAnalysis?: () => void;
};

export type MediaViewerRef = {
    toggleFullscreen: () => void;
};

export const MediaViewer = forwardRef<MediaViewerRef, MediaViewerProps>(
    function MediaViewer({
        item,
        isLoading,
        showOcrOverlay = false,
        onToggleAiAnalysis,
    }, ref) {
        const videoRef = useRef<HTMLVideoElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);
        const [isFullscreen, setIsFullscreen] = useState(false);

        useEffect(() => {
            if (videoRef.current) {
                videoRef.current.volume = 0.85;
            }
        }, [item?.id]);

        useEffect(() => {
            const handleFullscreenChange = () => {
                setIsFullscreen(
                    document.fullscreenElement === containerRef.current,
                );
            };

            document.addEventListener(
                "fullscreenchange",
                handleFullscreenChange,
            );
            return () => {
                document.removeEventListener(
                    "fullscreenchange",
                    handleFullscreenChange,
                );
            };
        }, []);

        const toggleFullscreen = useCallback(() => {
            if (!containerRef.current) return;

            if (!document.fullscreenElement) {
                containerRef.current.requestFullscreen().catch((err) => {
                    toast.error(`Failed to enter fullscreen: ${err.message}`);
                });
            } else {
                document.exitFullscreen();
            }
        }, []);

        useImperativeHandle(ref, () => ({
            toggleFullscreen,
        }), [toggleFullscreen]);

        if (isLoading) {
            return (
                <div
                    className="flex items-center justify-center w-full"
                    style={{
                        minHeight: "60vh",
                    }}
                >
                    <Skeleton className="w-full max-w-2xl aspect-video" />
                </div>
            );
        }

        if (!item) {
            return (
                <div
                    className="flex items-center justify-center w-full text-text-secondary"
                    style={{
                        minHeight: "60vh",
                    }}
                >
                    <p>No media to display</p>
                </div>
            );
        }

        const downloadExtension = getFileExtension(
            item.media_url,
            item.media_type,
        );

        return (
            <div
                ref={containerRef}
                className={`flex flex-col items-center gap-4 w-full ${
                    isFullscreen
                        ? "bg-black fixed inset-0 z-50 justify-center p-4"
                        : ""
                }`}
            >
                <div className="flex items-center justify-center w-full relative">
                    {item.media_type === "video"
                        ? (
                            <video
                                ref={videoRef}
                                key={item.id}
                                src={item.media_url}
                                controls
                                autoPlay
                                loop
                                playsInline
                                className={`rounded-lg ${
                                    isFullscreen
                                        ? "max-h-[90vh] max-w-[95vw]"
                                        : "max-h-[60vh] max-w-full"
                                }`}
                            />
                        )
                        : (
                            <img
                                key={item.id}
                                src={item.media_url}
                                alt={`Media ${item.id}`}
                                className={`rounded-lg object-contain ${
                                    isFullscreen
                                        ? "max-h-[90vh] max-w-[95vw]"
                                        : "max-h-[60vh] max-w-full"
                                }`}
                                loading="eager"
                            />
                        )}

                    <div className="absolute top-2 right-2 flex gap-2">
                        {item.ai_analyses &&
                            Object.keys(item.ai_analyses).length > 0 &&
                            onToggleAiAnalysis && (
                            <button
                                type="button"
                                onClick={onToggleAiAnalysis}
                                className={`p-2 rounded-lg transition-colors ${
                                    isFullscreen
                                        ? "bg-white/20 hover:bg-white/30 text-white"
                                        : "bg-black/40 hover:bg-black/60 text-white"
                                } cursor-pointer`}
                                title="AI Analysis (I)"
                            >
                                <Sparkles size={20} />
                            </button>
                        )}
                        <a
                            href={item.media_url}
                            download={`spl-${item.id}.${downloadExtension}`}
                            className={`p-2 rounded-lg transition-colors ${
                                isFullscreen
                                    ? "bg-white/20 hover:bg-white/30 text-white"
                                    : "bg-black/40 hover:bg-black/60 text-white"
                            } cursor-pointer`}
                            title="Download"
                        >
                            <Download size={20} />
                        </a>
                        <button
                            type="button"
                            onClick={toggleFullscreen}
                            className={`p-2 rounded-lg transition-colors ${
                                isFullscreen
                                    ? "bg-white/20 hover:bg-white/30 text-white"
                                    : "bg-black/40 hover:bg-black/60 text-white"
                            } cursor-pointer`}
                            title={isFullscreen
                                ? "Exit fullscreen (F)"
                                : "Fullscreen (F)"}
                        >
                            {isFullscreen
                                ? <Minimize2 size={20} />
                                : <Maximize2 size={20} />}
                        </button>
                    </div>

                    {showOcrOverlay && item.ocr_text && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-lg overflow-auto p-4">
                            <div className="max-w-full max-h-full text-white text-sm font-mono whitespace-pre-wrap">
                                {item.ocr_text}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    },
);
